/**
 * SchedulerStatusCard — Task Queue status widget for admin dashboard.
 * Shows scheduler health, pending task count, and recent history.
 */

import { useState, useEffect, useCallback } from 'react';
import { Activity, Clock, CheckCircle, AlertTriangle, Pause, Play, RefreshCw } from 'lucide-react';

interface SchedulerStatus {
  running: boolean;
  paused: boolean;
  lastTick: string | null;
  lastResult: { processed: number; failed: number; skipped: number } | null;
  totalTicks: number;
}

interface TaskSummary {
  id: string;
  type: string;
  template: string;
  status: string;
  action_time: string;
  retries: number;
  recurrence: string | null;
  source: string;
}

interface StatusResponse {
  scheduler: SchedulerStatus;
  queue: { pending: number; tasks: TaskSummary[] };
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

export function SchedulerStatusCard() {
  const [status, setStatus] = useState<StatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/scheduler/status', { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch scheduler status');
      const data = await res.json();
      setStatus(data);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handlePauseResume = async () => {
    if (!status) return;
    const action = status.scheduler.paused ? 'resume' : 'pause';
    await fetch(`/api/admin/scheduler/${action}`, { method: 'POST', headers: getAuthHeaders() });
    fetchStatus();
  };

  const handleManualTick = async () => {
    await fetch('/api/admin/scheduler/run', { method: 'POST', headers: getAuthHeaders() });
    fetchStatus();
  };

  const formatTime = (iso: string | null) => {
    if (!iso) return 'Never';
    const d = new Date(iso);
    const now = Date.now();
    const diffMs = now - d.getTime();
    if (diffMs < 60000) return 'Just now';
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
    return d.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="scheduler-card">
        <div className="scheduler-card-header">
          <Activity size={20} />
          <span>Task Queue</span>
        </div>
        <div className="scheduler-card-body">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="scheduler-card scheduler-card-error">
        <div className="scheduler-card-header">
          <AlertTriangle size={20} />
          <span>Task Queue</span>
        </div>
        <div className="scheduler-card-body">{error}</div>
      </div>
    );
  }

  const s = status!.scheduler;
  const q = status!.queue;

  return (
    <div className="scheduler-card">
      <div className="scheduler-card-header">
        <Activity size={20} />
        <span>Task Queue</span>
        <span className={`scheduler-badge ${s.paused ? 'paused' : s.running ? 'running' : 'stopped'}`}>
          {s.paused ? 'Paused' : s.running ? 'Running' : 'Stopped'}
        </span>
      </div>

      <div className="scheduler-card-body">
        <div className="scheduler-stats">
          <div className="scheduler-stat">
            <Clock size={16} />
            <span className="stat-label">Last Tick</span>
            <span className="stat-value">{formatTime(s.lastTick)}</span>
          </div>
          <div className="scheduler-stat">
            <Activity size={16} />
            <span className="stat-label">Pending</span>
            <span className="stat-value">{q.pending}</span>
          </div>
          <div className="scheduler-stat">
            <CheckCircle size={16} />
            <span className="stat-label">Total Ticks</span>
            <span className="stat-value">{s.totalTicks}</span>
          </div>
          {s.lastResult && (s.lastResult.processed > 0 || s.lastResult.failed > 0) && (
            <div className="scheduler-stat">
              {s.lastResult.failed > 0 ? <AlertTriangle size={16} /> : <CheckCircle size={16} />}
              <span className="stat-label">Last Run</span>
              <span className="stat-value">
                {s.lastResult.processed} done{s.lastResult.failed > 0 ? `, ${s.lastResult.failed} failed` : ''}
              </span>
            </div>
          )}
        </div>

        {q.pending > 0 && (
          <div className="scheduler-pending">
            <div className="pending-header">Pending Tasks:</div>
            {q.tasks.slice(0, 5).map(t => (
              <div key={t.id} className="pending-task"
                title={`ID: ${t.id}\nType: ${t.type}/${t.template}\nStatus: ${t.status}\nRetries: ${t.retries}\nSource: ${t.source}\nAction Time: ${t.action_time}${t.recurrence ? '\nRecurrence: ' + t.recurrence : ''}`}
              >
                <span className="task-type">{t.type}/{t.template}</span>
                <span className="task-retries">{t.retries > 0 ? `retry ${t.retries}` : ''}</span>
                <span className="task-time">{formatTime(t.action_time)}</span>
              </div>
            ))}
            {q.tasks.length > 5 && <div className="pending-more">+{q.tasks.length - 5} more</div>}
          </div>
        )}

        <div className="scheduler-actions">
          <button className="scheduler-btn" onClick={handlePauseResume} title={s.paused ? 'Resume' : 'Pause'}>
            {s.paused ? <Play size={14} /> : <Pause size={14} />}
            {s.paused ? 'Resume' : 'Pause'}
          </button>
          <button className="scheduler-btn" onClick={handleManualTick} title="Run tick now">
            <RefreshCw size={14} />
            Run Now
          </button>
        </div>
      </div>

      <style>{`
        .scheduler-card {
          background: var(--bg-primary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-large);
          overflow: hidden;
        }
        .scheduler-card-error {
          border-color: var(--accent-red);
        }
        .scheduler-card-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border-secondary);
          font-weight: 600;
          font-size: 15px;
          color: var(--text-primary);
        }
        .scheduler-badge {
          margin-left: auto;
          font-size: 11px;
          font-weight: 600;
          padding: 3px 10px;
          border-radius: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .scheduler-badge.running { background: #10b98120; color: #10b981; }
        .scheduler-badge.paused { background: #facc1520; color: #facc15; }
        .scheduler-badge.stopped { background: #ef444420; color: #ef4444; }
        .scheduler-card-body {
          padding: 16px 20px;
        }
        .scheduler-stats {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .scheduler-stat {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--text-secondary);
          font-size: 13px;
        }
        .stat-label { flex: 1; }
        .stat-value { font-weight: 600; color: var(--text-primary); }
        .scheduler-pending {
          margin-top: 16px;
          padding-top: 12px;
          border-top: 1px solid var(--border-secondary);
        }
        .pending-header {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-tertiary);
          text-transform: uppercase;
          margin-bottom: 8px;
        }
        .pending-task {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          padding: 4px 0;
          color: var(--text-secondary);
        }
        .task-type { font-family: monospace; font-size: 12px; flex: 1; }
        .task-retries { font-size: 10px; color: #f59e0b; font-weight: 600; }
        .task-time { color: var(--text-tertiary); font-size: 12px; }
        .pending-more { font-size: 12px; color: var(--text-tertiary); padding-top: 4px; }
        .scheduler-actions {
          display: flex;
          gap: 8px;
          margin-top: 16px;
          padding-top: 12px;
          border-top: 1px solid var(--border-secondary);
        }
        .scheduler-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          font-size: 12px;
          font-weight: 500;
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          background: var(--bg-secondary);
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.15s;
        }
        .scheduler-btn:hover {
          background: var(--bg-tertiary);
          color: var(--text-primary);
        }
      `}</style>
    </div>
  );
}
